import React from 'react';

import { Button, Modal, ModalBody, ModalHeader } from '@change/ds-core-ui';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';

/**
 * Modal component for confirming endorsement deletion.
 * Displays a confirmation dialog with delete and cancel actions.
 * Automatically transforms to a bottom sheet on mobile devices.
 */
export const DeleteEndorsementModal: React.FC<DeleteEndorsementModalProps> = ({
	isOpen,
	onOpenChange,
	onConfirmDelete,
	isDeleting = false,
}) => {
	const { translate } = useI18n();

	const handleDelete = async () => {
		await onConfirmDelete();
		onOpenChange?.(false);
	};

	const handleClose = () => {
		onOpenChange?.(false);
	};

	return (
		<Modal
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			isDismissable
			size="small"
			onClose={handleClose}
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
			data-testid="delete-endorsement-modal"
			className="[&>div]:gap-8 md:[&>div>div]:min-h-0"
		>
			<ModalHeader title={translate('corgi.petition.edit.endorsements.delete_modal.title')} />
			<ModalBody className="md:h-6">
				{({ close }) => (
					<div className="flex w-full flex-col gap-3 md:flex-row md:justify-end md:gap-4">
						<Button
							variant="secondary"
							onClick={close}
							data-testid="cancel-delete-endorsement-button"
							className="w-full md:w-auto"
							disabled={isDeleting}
						>
							<Translate value="corgi.petition.edit.endorsements.delete_modal.cancel_button" />
						</Button>
						<Button
							variant="primary"
							onClick={handleDelete}
							data-testid="confirm-delete-endorsement-button"
							className="w-full md:w-auto"
							disabled={isDeleting}
						>
							<Translate value="corgi.petition.edit.endorsements.delete_modal.confirm_button" />
						</Button>
					</div>
				)}
			</ModalBody>
		</Modal>
	);
};

type DeleteEndorsementModalProps = Readonly<{
	/** Whether the modal is open (controlled mode) */
	isOpen?: boolean;
	/** Callback when the modal is closed */
	onOpenChange?: (isOpen: boolean) => void;
	/** Callback when delete is confirmed */
	onConfirmDelete: () => void | Promise<void>;
	/** Whether the delete operation is in progress */
	isDeleting?: boolean;
}>;
